import delv from 'dlv';

import {
  ICartEntry,
  IItem,
  IItemOption,
  IMainMenuPickerView,
  IMainMenuSectionView,
  IOffer,
  IPickerOption,
  IPrices,
  ISanityCombo,
  ISanityComboSlot,
  ISanityPickerOption,
  IVendorConfig,
  MenuObject,
  SanityMenuObject
} from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { IDayPartBoundary } from 'state/day-part/hooks/use-active-day-parts';
import { CartEntryType } from 'utils/cart';

import {
  IWithVendorConfig,
  PluTypes,
  PosVendors,
  concatenateSizePlu,
  getQuantityBasedPlu,
  getVendorConfig
} from '../vendor-config';

import { itemOptionModifierPluIsAvailable } from './item-option-modifier-plu-is-available';
import { IItemOptionModifierIsAvailable } from './types';

import { availabilityLogger } from '..';

interface IAvailabilityConfig {
  injectDefault?: boolean;
  isMainItem?: boolean;
  quantity?: number;
}

type AvailabilityFn<D> = (
  data: D,
  vendor: PosVendors | null,
  prices: IPrices,
  options?: IAvailabilityConfig
) => boolean;

const getVendorConfigForData = (
  item: IWithVendorConfig,
  vendor: PosVendors | null
): IVendorConfig | null => {
  if (!item || !item.vendorConfigs) {
    availabilityLogger(
      item,
      `Missing vendorConfigs for ${delv(item, 'name.locale', 'Name unknown')}`
    );
    return null;
  }

  const vendorConfig = getVendorConfig(item, vendor);

  if (!vendorConfig) {
    availabilityLogger(item, `Missing vendorConfig for ${vendor}`);
    return null;
  }

  return vendorConfig;
};

export const pluIsAvailable = (plu: string | null, prices: IPrices): boolean =>
  !!plu && plu in prices && !isNaN(+prices[plu]);

export const vendorConfigIsAvailable = (
  vendorConfig: IVendorConfig,
  prices: IPrices,
  options: IAvailabilityConfig = {}
): boolean => {
  switch (vendorConfig.pluType) {
    case PluTypes.CONSTANT:
      return pluIsAvailable(vendorConfig.constantPlu, prices);
    case PluTypes.IGNORE:
      return true;
    case PluTypes.MULTI_CONSTANT:
      return (vendorConfig.multiConstantPlus || []).every(({ plu }) => pluIsAvailable(plu, prices));
    case PluTypes.PARENT_CHILD:
      return (
        pluIsAvailable(vendorConfig.parentChildPlu.plu, prices) &&
        pluIsAvailable(vendorConfig.parentChildPlu.childPlu, prices)
      );
    case PluTypes.QUANTITY:
      return pluIsAvailable(
        getQuantityBasedPlu(vendorConfig.quantityBasedPlu, options.quantity),
        prices
      );
    case PluTypes.SIZE_BASED:
      return pluIsAvailable(concatenateSizePlu(vendorConfig.sizeBasedPlu), prices);
    default:
      return false;
  }
};

export const itemIsAvailable: AvailabilityFn<IWithVendorConfig> = (
  item,
  vendor,
  prices = {},
  options: IAvailabilityConfig = {}
) => {
  const vendorConfig = getVendorConfigForData(item, vendor);

  if (!vendorConfig) {
    return false;
  }

  const available = vendorConfigIsAvailable(vendorConfig, prices, options);

  if (!available) {
    availabilityLogger(item, 'Item is currently marked unavailable.');
    return false;
  }

  return true;
};

export const comboSlotIsAvailable: AvailabilityFn<ISanityComboSlot> = (
  comboSlot,
  vendor,
  prices
) => {
  if (!itemIsAvailable(comboSlot, vendor, prices)) {
    return false;
  }

  const minRequirement = comboSlot.minAmount;

  const availableOptions = comboSlot.options.reduce(
    (acc, option) =>
      itemIsAvailable(option.option, vendor, prices, {
        quantity: comboSlot.minAmount || 1
      })
        ? acc + option.maxAmount
        : acc,
    0
  );

  if (availableOptions < minRequirement) {
    availabilityLogger(
      comboSlot,
      `${comboSlot.name?.locale || ''} does not have minimum availability requirements.`
    );
    return false;
  }

  return true;
};

export const comboIsAvailable: AvailabilityFn<ISanityCombo> = (combo, vendor, prices) => {
  if (!itemIsAvailable(combo, vendor, prices)) {
    return false;
  }

  if (combo.mainItem && !itemIsAvailable(combo.mainItem, vendor, prices)) {
    availabilityLogger(combo.mainItem, 'Main item missing');
    return false;
  }

  const allComboSlotsAvailable = combo.options.every(comboSlot => {
    return comboSlotIsAvailable(comboSlot, vendor, prices);
  });

  if (!allComboSlotsAvailable) {
    availabilityLogger(combo, 'Not all combo slots were available.');
    return false;
  }

  return true;
};

export const itemOptionModifierIsAvailable = ({
  item,
  itemOption,
  itemOptionModifier,
  prices,
  vendor
}: IItemOptionModifierIsAvailable & { itemOption: IItemOption }): boolean => {
  const pluIsAvailableForItemOptionModifier = itemOptionModifierPluIsAvailable({
    item,
    itemOptionModifier,
    prices,
    vendor
  });

  // for item option modifiers we should only care about plu availability when
  // "inject default selection" is true, OR the item is not the default.
  // if "inject default selection" is false, default modifiers are assumed to
  // be available if the item they belong to is available.
  if (itemOption.injectDefaultSelection) {
    return pluIsAvailableForItemOptionModifier;
  }

  return itemOptionModifier.default || pluIsAvailableForItemOptionModifier;
};

interface IItemOptionIsAvailable {
  item: IItem;
  itemOption: IItemOption;
  vendor: PosVendors;
  prices: IPrices;
}

export const itemOptionIsAvailable = ({
  item,
  itemOption,
  vendor,
  prices
}: IItemOptionIsAvailable): boolean => {
  if (!itemOption || !itemOption.options || itemOption.options.length === 0) {
    availabilityLogger(itemOption, 'No options found');
    return false;
  }

  const availableOptions: number = itemOption.options.filter(itemOptionModifier =>
    itemOptionModifierIsAvailable({
      item,
      itemOption,
      itemOptionModifier,
      prices,
      vendor
    })
  ).length;

  return availableOptions > 0;
};

// this "availability" is actually very different from
// availability of IItem and friends
export const cartEntryIsAvailable = (
  cartEntry: ICartEntry,
  posVendor: PosVendors | null,
  prices: IPrices,
  item?: ICartEntry
): boolean => {
  const isAvailable = (entry: ICartEntry) => {
    switch (entry.type) {
      case CartEntryType.combo:
        // we don't use comboIsAvailable here
        return itemIsAvailable(entry, posVendor, prices);
      case CartEntryType.item:
        return itemIsAvailable(entry, posVendor, prices);
      case CartEntryType.itemOptionModifier:
        // we only really need to know if the plu is available
        if (!item || !posVendor) {
          return false;
        }
        return itemOptionModifierPluIsAvailable({
          item,
          itemOptionModifier: entry,
          vendor: posVendor,
          prices
        });
      case CartEntryType.comboSlot:
      case CartEntryType.itemOption:
        return true;
      case CartEntryType.offerCombo:
      case CartEntryType.offerItem:
        return false;
      default:
        return false; // this shouldn't happen
    }
  };

  // hacking in a way to remember what the associated IItem is at every step
  const i = cartEntry.type === CartEntryType.item ? cartEntry : item;

  return (
    isAvailable(cartEntry) &&
    (cartEntry.children || []).every(entry => cartEntryIsAvailable(entry, posVendor, prices, i))
  );
};

const dayPartsContainActiveDayPart = (dayparts: string[], activeDayParts: IDayPartBoundary[]) =>
  activeDayParts.findIndex(({ key }) => dayparts.includes(key.toLowerCase())) !== -1;

export type IMenuItem =
  | SanityMenuObject
  | MenuObject
  | IMainMenuPickerView
  | IMainMenuSectionView
  | IOffer
  | null;

export const getMenuItemDayParts = (menuData: IMenuItem): string[] => {
  if (!menuData) {
    return [];
  }
  switch (menuData._type) {
    case 'item': {
      const dayParts: string[] = (
        delv(menuData, 'operationalItem.daypart') || []
      ).map((daypart: string) => daypart.toLowerCase());
      return dayParts;
    }
    case 'combo': {
      const mainItem = menuData.mainItem;
      return getMenuItemDayParts(mainItem);
    }
    case 'offer': {
      // We currently do not set day parts on items from MDM, so allow setting
      // dayparts on coupons
      // @todo Remove once we pull dayparts off items
      const dayParts: string[] = (menuData.daypart || []).map((daypart: string) =>
        daypart.toLowerCase()
      );
      return dayParts;
    }
    case 'section': {
      if (menuData.daypart) {
        const dayParts: string[] = menuData.daypart.map((daypart: string) => daypart.toLowerCase());
        return dayParts;
      }

      return [];
    }
    default:
      return [];
  }
};

/**
 * Determine whether a menu object can be ordered
 * during the given activeDayPart(s).
 */
export function isAvailableForActiveDayParts({
  activeDayParts,
  menuData
}: {
  activeDayParts: IDayPartBoundary[];
  menuData: IMenuItem;
}): boolean {
  if (!menuData) {
    return false;
  }

  const dayParts = getMenuItemDayParts(menuData);

  const isDayPartsQualify = (dayPartsToCheck: string[]) =>
    !dayPartsToCheck.length || dayPartsContainActiveDayPart(dayPartsToCheck, activeDayParts);

  if (menuData._type === 'offer') {
    const option = menuData.option;
    if (option && option._type !== MenuObjectTypes.OFFER_DISCOUNT) {
      const optionDayParts = getMenuItemDayParts(option as SanityMenuObject);

      return isDayPartsQualify(dayParts) && isDayPartsQualify(optionDayParts);
    }
  }

  if (menuData._type === 'picker' && 'options' in menuData) {
    const options: IPickerOption[] | ISanityPickerOption[] = menuData.options || [];

    return options.some((option: IPickerOption | ISanityPickerOption) => {
      const optionDayParts = getMenuItemDayParts(option.option);
      return isDayPartsQualify(optionDayParts);
    });
  }

  return isDayPartsQualify(dayParts);
}
